import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import type { MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import type { Language } from "@/modules/language/utils/languageUtils"
import { useScoreActions } from "@/modules/scores/scoreForms/hooks/useScoreActions"
import { convertCommentFromDraft } from "@/modules/scores/scoreUtils"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { MAX_65535_CHARACTERS } from "@/typings/formik"
import { word } from "@modules/core/utils/i18n"
import { z } from "zod"

const commentSchema = (_language: Language) =>
  z.object({
    note: z
      .string()
      .min(1, word("global.form.required"))
      .max(MAX_65535_CHARACTERS, word("form.maximum.characters", { count: MAX_65535_CHARACTERS })),
    noteAndMediaIsVisible: z.boolean(),
    medias: z
      .array(
        z.object({
          url: z.string().nullable(),
          key: z.string().nullable(),
          file: z.instanceof(File).nullable(),
          type: z.enum(MediaType),
          isUploading: z.boolean(),
          filename: z.string().nullable(),
        }),
      )
      .max(5, word("sessionForm.blocks.add_media.wrong_quantity_of_media")),
  })

export type CommentFormFields = z.infer<typeof commentSchema>

export const useCommentFormik = (
  parentSession: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity,
  exercise: SessionBlockExerciseEntity,
) => {
  const { sidebarNavigateBack } = useSidebar()
  const { addMultipleScoresToExercise } = useScoreActions()
  const language = useLanguage()

  const form = useAppForm({
    defaultValues: {
      note: exercise.score?.note ?? "",
      noteAndMediaIsVisible: exercise.score?.noteAndMediaIsVisible ?? true,
      medias: (exercise.score?.medias?.map((media) => ({
        ...media,
        key: media.key ?? null,
        file: null,
        isUploading: false,
      })) ?? []) as MediaItemFormType[],
    },
    validators: {
      onChange: commentSchema(language),
      onSubmit: commentSchema(language),
      onBlur: commentSchema(language),
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        await addMultipleScoresToExercise({
          programId: parentSession.programId,
          sessionId: parentSession.id,
          exerciseId: exercise.id,
          score: convertCommentFromDraft(value),
        })

        toast.success(word("session.comment.update.success"), {
          classNames: successToastClassName(),
        })
        // Reset so the form is pristine and the sidebar onHide confirmation is skipped when navigating away
        formApi.reset()
        setTimeout(() => {
          sidebarNavigateBack()
        }, 500)
      } catch (e: unknown) {
        if (e instanceof Error) {
          toast.error(word("global.error.label"), {
            description: e.message,
            classNames: errorToastClassName(),
          })
        } else {
          toast.error(word("global.error.label"), {
            description: String(e),
            classNames: errorToastClassName(),
          })
        }
        console.error("Error on update a comment", e)
      }
    },
  })

  return { commentFormik: form }
}
